import { reactive } from 'vue'
import axios from 'axios'
import { load } from 'recaptcha-v3'
import { StoreBase } from '../StoreBase'

class PasswordChangeStore implements StoreBase {
  /* eslint-disable class-methods-use-this */
  createStore() {
    const state = reactive({
      userId: '',
      tokenId: '',
    })
    /**
     * ストアに格納されているログインIDかメールアドレスにより、パスワード再発行メール送信をAPIに要求する。
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1567064246/URL+API
     * @return 送信結果。
     */
    const sendResetPasswordMail = async (data: { apiBaseUrl: string; reCaptchaToken: string }) => {
      const requestType = state.userId.indexOf('@') === -1 ? 'username' : 'mailAddress'
      const result = await axios
        .post(
          `${data.apiBaseUrl}/manage/password_reset_token`,
          {
            reCaptchaToken: data.reCaptchaToken,
            [requestType]: state.userId,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(res => {
          return { isSuccess: true, res }
        })
        .catch(error => {
          let errRes = error
          // ネットワークエラー対策
          if (!errRes.response) {
            // eslint-disable-next-line @typescript-eslint/camelcase
            errRes = { response: { data: { error_code: 'NETWORK_ERROR' } } }
          }
          // eslint-disable-next-line prefer-promise-reject-errors
          return { isSuccess: false, res: errRes }
        })
      return result
    }

    /**
     * reCaptchaトークンを取得する
     * 取得したtokenはストアに保存される
     * @param action トークン取得時に指定するAction
     * @return トークン
     */
    const getReCaptchaToken = async (action = 'getToken') => {
      try {
        const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY)
        const token = await recaptcha.execute(action)
        return token
      } catch {
        return ''
      }
    }

    /**
     * reCaptchaのトークンを取得し、ストアに格納されている値で
     * パスワードリセットURL送信APIを実行する
     * APIの仕様に関しては以下を参照する
     * @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/1567064246/URL+API
     *
     */
    const getTokenAndSendResetPasswordMail = async () => {
      const allApiBase = JSON.parse(process.env.VUE_APP_ALL_API_BASE_URL || {})
      const apiBaseUrlList =
        typeof allApiBase === 'string' ? [allApiBase] : (Object.values(allApiBase) as string[])

      const promises = apiBaseUrlList.map(async apiBaseUrl => {
        const reCaptchaToken = await getReCaptchaToken()
        // reCaptchaのトークン取得に失敗した場合、apiEventを作成して処理を終了する。
        if (!reCaptchaToken) {
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.resolve({
            isSuccess: false,
            // eslint-disable-next-line @typescript-eslint/camelcase
            res: { response: { data: { error_code: 'NETWORK_ERROR' } } },
          })
        }
        return sendResetPasswordMail({ apiBaseUrl, reCaptchaToken })
      })
      const apiEvents = await Promise.all(promises)
      const successList = apiEvents.filter(apiEvent => apiEvent.isSuccess) || []
      if (successList.length > 0) {
        return successList[0]
      }
      const errorList = apiEvents.filter(apiEvent => !apiEvent.isSuccess) || []
      // eslint-disable-next-line @typescript-eslint/camelcase
      return errorList[0]
    }

    return {
      getTokenAndSendResetPasswordMail,
      /**
       * 入力ユーザ情報(mailAddress or loginId)
       */
      get userId() {
        return state.userId
      },
      /**
       * 入力ユーザ情報(mailAddress or loginId)
       * @param userId 入力ユーザ情報(mailAddress or loginId)
       */
      set userId(userId: string) {
        state.userId = userId
      },
      /**
       * tokenId
       */
      get tokenId() {
        return state.tokenId
      },
      /**
       * tokenId
       * @param tokenId
       */
      set tokenId(tokenId: string) {
        state.tokenId = tokenId
      },
    }
  }
  /* eslint-enable class-methods-use-this */
}

export default new PasswordChangeStore().createStore()
